
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Workspace, WorkspaceContextType } from '@/types/workspace';
import { useAuth } from './AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { getAuthToken } from '@/utils/appsettings';
import { onCreditUpdate } from '@/utils/creditEvents';

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

// Set just before a tab reloads to follow another tab's switch, so it can say why it changed workspace
const SYNCED_FROM_TAB_KEY = 'joylo_workspace_synced_from_tab';

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, loading: authLoading } = useAuth();
    const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
    const [pendingInvites, setPendingInvites] = useState<Workspace[]>([]);
    const [activeWorkspace, setActiveWorkspace] = useState<Workspace | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const { toast } = useToast();

    const fetchWorkspaces = async (): Promise<Workspace[]> => {
        if (!isAuthenticated) return [];

        try {
            setIsLoading(true);
            const token = getAuthToken();
            if (!token) return [];

            const [wsResponse, invitesResponse] = await Promise.all([
                fetch(`${import.meta.env.VITE_BASE_API_URL}/workspaces/`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }),
                fetch(`${import.meta.env.VITE_BASE_API_URL}/workspaces/invites/pending`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                })
            ]);

            if (invitesResponse.ok) {
                const invitesData = await invitesResponse.json();
                setPendingInvites(invitesData);
            } else {

                // toast({
                //     title: "Error fetching invites",
                //     description: "There was a problem loading your pending invites.",
                //     variant: "destructive",
                // });
            }

            if (wsResponse.ok) {
                const data = await wsResponse.json();
                setWorkspaces(data);

                // Restore active workspace or default to personal
                const savedWsId = localStorage.getItem('joylo_active_workspace_id');
                let nextWorkspace = null;
                let wasRevoked = false;

                if (savedWsId) {
                    const saved = data.find((w: Workspace) => String(w.id) === String(savedWsId));
                    if (saved) {
                        nextWorkspace = saved;
                    } else {
                        // savedWsId is no longer in the workspace list — user was removed
                        wasRevoked = true;
                    }
                }

                // Fallback to personal or first found
                if (!nextWorkspace && data.length > 0) {
                    // Prefer personal if available
                    const personal = data.find((w: Workspace) => w.type === 'personal');
                    nextWorkspace = personal || data[0];
                }

                if (nextWorkspace) {
                    setActiveWorkspace(nextWorkspace);
                    localStorage.setItem('joylo_active_workspace_id', String(nextWorkspace.id));

                    let syncedName: string | null = null;
                    try {
                        syncedName = sessionStorage.getItem(SYNCED_FROM_TAB_KEY);
                        sessionStorage.removeItem(SYNCED_FROM_TAB_KEY);
                    } catch {
                        syncedName = null;
                    }

                    if (syncedName !== null) {
                        toast({
                            title: "Workspace switched in another tab",
                            description: `This tab now shows ${nextWorkspace.name} too.`,
                        });
                    } else if (wasRevoked) {
                        // Not an error, but say why: the outsider read the silent switch as nothing happening (UAT 1029l8qb F9)
                        toast({
                            title: "You no longer have access to that workspace",
                            description: `Opened ${nextWorkspace.name} instead.`,
                        });
                    }
                }
                return data;
            } else {
                return [];
            }
        } catch (error) {
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // Wait for AuthContext session restore before deciding what to do.
        // Otherwise on a hard refresh we'd race the token hydration and fire
        // /workspaces/ with a stale or missing Authorization header, returning 401.
        if (authLoading) return;

        if (isAuthenticated) {
            fetchWorkspaces();
        } else {
            setWorkspaces([]);
            setPendingInvites([]);
            setActiveWorkspace(null);
            setIsLoading(false);
        }
    }, [authLoading, isAuthenticated]);

    // Plan gates read activeWorkspace.subscription, so a billing change must re-read it in place; no loading flash, same active workspace
    useEffect(() => {
        if (!isAuthenticated) return;
        let inFlight = false;
        return onCreditUpdate(async (source) => {
            if (source !== 'billing-success' && source !== undefined) return;
            const token = getAuthToken();
            if (inFlight || !token) return;
            inFlight = true;
            try {
                const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/workspaces/`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
                });
                if (!response.ok) return;
                const data: Workspace[] = await response.json();
                setWorkspaces(data);
                setActiveWorkspace(prev => (prev && data.find(w => String(w.id) === String(prev.id))) || prev);
            } catch {
                // Best effort: the next full workspace fetch picks the plan up
            } finally {
                inFlight = false;
            }
        });
    }, [isAuthenticated]);

    const switchWorkspace =(workspaceId: string | number, workspacesOverride?: Workspace[]) => {
        const source = workspacesOverride || workspaces;
        const ws = source.find(w => String(w.id) === String(workspaceId));
        if (ws) {
            setActiveWorkspace(ws);
            localStorage.setItem('joylo_active_workspace_id', String(ws.id));
            toast({
                title: "Workspace Switched",
                description: `You are now working in ${ws.name}`,
            });
            // We might want to reload or invalidate queries here
            // For now, let's assume components will react to context change or we force reload if needed
            // window.location.reload(); 
        } else {
            toast({
                title: "Workspace Not Found",
                description: "The requested workspace could not be found.",
                variant: "destructive",
            });
        }
    };

    // The active workspace is one per browser: a tab left on the old one sent its actions there while the others showed the new one, so it reloads into the switch (UAT 1029l8qb F12)
    useEffect(() => {
        const onStorage = (event: StorageEvent) => {
            if (event.key !== 'joylo_active_workspace_id' || !event.newValue || !activeWorkspace) return;
            if (String(event.newValue) === String(activeWorkspace.id)) return;
            const target = workspaces.find(w => String(w.id) === String(event.newValue));
            try {
                sessionStorage.setItem(SYNCED_FROM_TAB_KEY, target?.name ?? '');
            } catch {
                // The reload still syncs the workspace; only the explanation is lost
            }
            window.location.reload();
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [workspaces, activeWorkspace]);

    const createWorkspace = async (name: string): Promise<{ newWorkspace: Workspace, updatedWorkspaces: Workspace[] }> => {
        const token = getAuthToken();
        const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/workspaces/`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ name })
        });

        if (!response.ok) {
            throw new Error("Failed to create workspace");
        }

        const newWorkspace = await response.json();
        const updatedWorkspaces = await fetchWorkspaces(); // Refresh list
        return { newWorkspace, updatedWorkspaces };
    };

    const acceptInvite = async (inviteToken: string) => {
        const token = getAuthToken();
        const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/workspaces/accept-invite`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ token: inviteToken })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || "Failed to accept invite");
        }

        const data = await response.json();
        const updatedWorkspaces = await fetchWorkspaces(); // Refresh to get the new workspace in the list

        toast({
            title: "Invite Accepted",
            description: `You have successfully joined ${data.workspace_name}.`,
        });

        // Switch to the new workspace automatically
        if (data.workspace_id) {
            switchWorkspace(data.workspace_id, updatedWorkspaces);
        }
    };

    return (
        <WorkspaceContext.Provider value={{
            workspaces,
            pendingInvites,
            activeWorkspace,
            isLoading,
            switchWorkspace,
            createWorkspace,
            acceptInvite,
            refreshWorkspaces: fetchWorkspaces
        }}>
            {children}
        </WorkspaceContext.Provider>
    );
}

export function useWorkspace() {
    const context = useContext(WorkspaceContext);
    if (context === undefined) {
        throw new Error('useWorkspace must be used within a WorkspaceProvider');
    }
    return context;
}
